import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Upload, CheckCircle, PlayCircle, FileText, BrainCircuit, Layers, GitPullRequest } from 'lucide-react';
import { Link } from 'react-router-dom';
import useAuthStore from '../../stores/authStore';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const AdminDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();

  const [totalProjects, setTotalProjects] = useState<number | null>(null);
  const [totalQAPairs, setTotalQAPairs] = useState<number | null>(null);
  const [activeModels, setActiveModels] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectsResponse, qaPairsResponse, modelsResponse] = await Promise.all([
          api.get('/projects/stats/total'),
          api.get('/qa_pairs/stats/total'),
          api.get('/models/active'), // Assuming this returns a list, we'll count its length
        ]);
        setTotalProjects(projectsResponse.data);
        setTotalQAPairs(qaPairsResponse.data);
        setActiveModels(modelsResponse.data.length);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to fetch dashboard data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-text-primary">{t('home.welcome', 'Welcome, {{name}}!', { name: user?.name || 'User' })}</h1>
        <p className="mt-2 text-text-secondary">{t('home.overview', "Here's a quick overview of the platform's status.")}</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : totalProjects}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total QA Pairs</CardTitle>
            <GitPullRequest className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : totalQAPairs}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Models</CardTitle>
            <BrainCircuit className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : activeModels}</div>
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="text-2xl font-semibold">{t('home.quickActions', 'Quick Actions')}</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <Link to="/documents">
            <Button size="lg" className="w-full justify-start">
              <Upload className="mr-2 h-4 w-4" />
              {t('home.actions.uploadDocument', 'Upload a Document')}
            </Button>
          </Link>
          <Link to="/validate">
            <Button size="lg" className="w-full justify-start">
              <CheckCircle className="mr-2 h-4 w-4" />
              {t('home.actions.startValidating', 'Start Validating')}
            </Button>
          </Link>
          <Link to="/playground">
            <Button size="lg" className="w-full justify-start">
              <PlayCircle className="mr-2 h-4 w-4" />
              {t('home.actions.testModel', 'Test a Model')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;